import React, { useCallback, useEffect, useState } from 'react'
import { Button, Layout, Space, message, Badge, Modal, Typography, notification } from 'antd'
import { Participant as IParticipant, LocalVideoTrack } from 'twilio-video'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faDesktop,
	faMicrophone,
	faMicrophoneSlash,
	faVideo,
	faVideoSlash,
	faPhone,
	faComments,
	faPlusCircle,
	faExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { useCallContext } from '../../../context/call/CallProvider'
import { useUnload } from '../../../context/utils'
import RoomChat from './RoomChat/RoomChat'

import './room.less'
import ReportIssueModal from './ReportIssueModal'
import { callApi, TApiResponse } from '../../../context/api'
import Participants from './Participants/Participants'

const { Paragraph } = Typography

function Room() {
	const { call, actions } = useCallContext()
	const [participants, setParticipants] = useState<IParticipant[]>([])
	const [screenTrack, setScreenTrack] = useState<LocalVideoTrack>()
	const [visibleReportIssueModal, setVisibleReportIssueModal] = useState<boolean>(false)
	const [dominantSpeaker, setDominantSpeaker] = useState<string>(null)
	const [roomChatOptions, setRoomChatOptions] = useState<{
		isOpen: boolean
		isNewMessages: boolean
	}>({ isOpen: false, isNewMessages: false })
	const [devicePermissions, setDevicePermissions] = useState<{ camera: boolean; microphone: boolean }>({
		camera: true,
		microphone: true,
	})

	useUnload(() => {
		if (call.room) {
			actions.disconnect()
			call.room.disconnect()
		}
	})

	useEffect(() => {
		navigator.mediaDevices
			.getUserMedia({ audio: true })
			.then((stream) => {
				const audioTrack = stream.getAudioTracks()[0]
				audioTrack?.stop()

				setDevicePermissions((prevState) => ({
					...prevState,
					microphone: !!audioTrack,
				}))
			})
			.catch(() => {
				setDevicePermissions((prevState) => ({
					...prevState,
					microphone: false,
				}))
			})
		navigator.mediaDevices
			.getUserMedia({ video: true })
			.then((stream) => {
				const videoTrack = stream.getVideoTracks()[0]
				videoTrack?.stop()

				setDevicePermissions((prevState) => ({
					...prevState,
					camera: !!videoTrack,
				}))
			})
			.catch(() => {
				setDevicePermissions((prevState) => ({
					...prevState,
					camera: false,
				}))
			})
	}, [])

	// Room
	useEffect(() => {
		if (call.room) {
			const participantConnected = (participant: IParticipant) => {
				setParticipants((prevParticipants) => [...prevParticipants, participant])
			}

			const participantDisconnected = (participant: IParticipant) => {
				setParticipants((prevParticipants) => prevParticipants.filter((p) => p.sid !== participant.sid))
			}

			const dominantSpeakerChanged = (participant: IParticipant | null) => {
				setDominantSpeaker(participant?.sid || null)
			}

			const reconnecting = (error: { code: number }) => {
				if (error.code === 53001) {
					message.loading('Reconnecting your signaling connection...', 2)
				} else if (error.code === 53405) {
					message.loading('Reconnecting your media connection...', 2)
				} else {
					message.loading('Reconnecting...', 2)
				}
			}

			const reconnected = () => {
				message.success('Connected', 2)
			}

			const disconnected = (_room: any, error?: { code: number }) => {
				if (error) {
					if (error.code === 20104) {
						actions.setError('Signaling reconnection failed due to expired AccessToken...')
					} else if (error.code === 53000) {
						actions.setError('Signaling reconnection attempts exhausted...')
					} else if (error.code === 53204) {
						actions.setError('Signaling reconnection took too long...')
					} else {
						actions.setError('Disconnected...')
					}
					actions.disconnect()
				}
			}

			call.room.on('participantConnected', participantConnected)
			call.room.on('participantDisconnected', participantDisconnected)
			call.room.on('dominantSpeakerChanged', dominantSpeakerChanged)
			call.room.on('reconnecting', reconnecting)
			call.room.on('reconnected', reconnected)
			call.room.on('disconnected', disconnected)
			setParticipants([call.room.localParticipant])
			call.room.participants.forEach(participantConnected)
			return () => {
				setParticipants([])
				call.room.off('participantConnected', participantConnected)
				call.room.off('participantDisconnected', participantDisconnected)
				call.room.off('dominantSpeakerChanged', participantDisconnected)
				call.room.off('reconnecting', reconnecting)
				call.room.off('reconnected', reconnected)
				call.room.off('disconnected', disconnected)
			}
		}
	}, [call.room])

	useEffect(() => {
		if (call.room && call.room.localParticipant) {
			if (call.audioMuted) {
				call.room.localParticipant.audioTracks.forEach((audioTrack) => audioTrack.track.stop())
			} else {
				call.room.localParticipant.audioTracks.forEach((audioTrack) => audioTrack.track.restart())
			}
		}
		return () => {
			call.room?.localParticipant.audioTracks.forEach((audioTrack) => audioTrack.track.stop())
		}
	}, [call.audioMuted, call.room])

	useEffect(() => {
		if (call.room && call.room.localParticipant) {
			if (call.videoMuted) {
				call.room.localParticipant.videoTracks.forEach((videoTrack) => videoTrack.track.disable())
			} else {
				call.room.localParticipant.videoTracks.forEach((videoTrack) => {
					videoTrack.track.enable()
					videoTrack.track.restart()
				})
			}
		}
		return () => {
			call.room?.localParticipant.videoTracks.forEach((videoTrack) => videoTrack.track.stop())
		}
	}, [call.videoMuted, call.room])

	useEffect(() => {
		message.destroy()

		if (roomChatOptions.isOpen) {
			return
		}

		const devices = []
		if (call.audioMuted) {
			devices.push('Microphone')
		}
		if (call.videoMuted) {
			devices.push('Camera')
		}

		if (devices.length) {
			message.info(`${devices.join(' and ')} turned off`, 0)
		}
	}, [call.audioMuted, call.videoMuted, roomChatOptions.isOpen])

	const handleLeave = useCallback(() => {
		call.room.localParticipant.tracks.forEach((trackPub) => {
			if (trackPub.track.kind !== 'data') {
				trackPub.track.stop()
			}
		})
		call.room.emit('participantDisconnected', call.room.localParticipant)
		call.room.disconnect()
		setParticipants([])
		message.destroy()
		actions.disconnect()
	}, [actions, call.room])

	const handleShareScreen = useCallback(async () => {
		if (screenTrack) {
			const localTrackPublication = call.room?.localParticipant?.unpublishTrack(screenTrack)
			call.room?.localParticipant?.emit('trackUnpublished', localTrackPublication)
			screenTrack.stop()
			call.room.localParticipant.tracks.forEach((trackPub) => {
				if (trackPub.track.kind === 'video') {
					trackPub.track.enable()
				}
			})
			setScreenTrack(null)
		} else {
			const stream = await (navigator.mediaDevices as any).getDisplayMedia()
			const videoTrack = stream.getTracks()[0]

			videoTrack.onended = (event: { currentTarget: MediaStreamTrack } & Event) => {
				const trackId = event.currentTarget.id
				call.room.localParticipant.tracks.forEach((trackPub) => {
					if (trackPub.track.kind === 'video') {
						if (trackPub.track.id === trackId) {
							const trackPublication = call.room?.localParticipant?.unpublishTrack(trackPub.track)
							call.room?.localParticipant?.emit('trackUnpublished', trackPublication)
							trackPub.track.stop()
						} else {
							trackPub.track.enable()
						}
					}
				})
				setScreenTrack(null)
			}

			call.room.localParticipant.tracks.forEach((trackPub) => {
				if (trackPub.track.kind === 'video') {
					trackPub.track.disable()
				}
			})
			const newScreenTrack = new LocalVideoTrack(videoTrack)
			setScreenTrack(newScreenTrack)
			call.room?.localParticipant?.publishTrack(newScreenTrack, { priority: 'high' })
		}
	}, [call.room?.localParticipant, screenTrack])

	const onNewMessage = () => {
		setRoomChatOptions((prevState) => ({
			isOpen: prevState.isOpen,
			isNewMessages: !prevState.isOpen,
		}))
	}

	const onShowRoomChat = () => {
		setRoomChatOptions((prevState) => ({
			isOpen: !prevState.isOpen,
			isNewMessages: false,
		}))
	}

	const onAudioClick = () => {
		if (devicePermissions.microphone) {
			actions.audioMuteToggle()
		} else {
			Modal.info({
				title: 'Microphone is blocked',
				content: (
					<Paragraph>
						Call requires access to your microphone. Click the microphone blocked icon{' '}
						<FontAwesomeIcon icon={faMicrophone} /> in your browser address bar
					</Paragraph>
				),
				maskClosable: true,
				okText: 'Close',
			})
		}
	}

	const onVideoClick = () => {
		if (devicePermissions.camera) {
			actions.videoMuteToggle()
		} else {
			Modal.info({
				title: 'Camera is blocked',
				content: (
					<Paragraph>
						Call requires access to your camera. Click the camera blocked icon <FontAwesomeIcon icon={faVideo} /> in
						your browser address bar
					</Paragraph>
				),
				maskClosable: true,
				okText: 'Close',
			})
		}
	}

	const sendReport = async (data: { message: string; email: string; phone: string }) => {
		const request = {
			message: data.message,
			browser: navigator.userAgent,
			username: `${call.participant.firstName} ${call.participant.lastName}`,
			twilioRoomId: call.room.sid,
			email: data.email,
			phone: data.phone,
			microphone: {
				hasAccess: devicePermissions.microphone,
				turnedOn: !call.audioMuted,
			},
			camera: {
				hasAccess: devicePermissions.camera,
				turnedOn: !call.videoMuted,
			},
		}

		const response: TApiResponse = await callApi(`/call/${call.call.id}/issue`, {
			method: 'POST',
			body: request,
		})
		if (response.ok) {
			notification.success({ message: 'Thank you for your request' })
		} else {
			notification.error({ message: `An error occurred: ${response.payload?.data?.message}` })
		}

		setVisibleReportIssueModal(false)
	}

	return (
		<Layout className="room-layout">
			<div className="room-main">
				<div className="room-container">
					<Participants
						participants={participants}
						dominantSpeaker={dominantSpeaker}
						localParticipantSid={call?.room?.localParticipant?.sid}
					/>
					<div className={!roomChatOptions.isOpen ? 'hidden' : 'chat-section'}>
						<RoomChat
							onClose={() =>
								setRoomChatOptions({
									isOpen: false,
									isNewMessages: false,
								})
							}
							onNewMessage={onNewMessage}
						/>
					</div>
				</div>
				<div className="room-actions">
					<span className="recording-warning">This call is being recorded</span>
					<Space size="large">
						<Button
							shape="circle"
							icon={<FontAwesomeIcon icon={faDesktop} color={!screenTrack ? '#8C8C8C' : '#197FF2'} />}
							size="large"
							onClick={handleShareScreen}
						/>
						<Button
							shape="circle"
							icon={
								<FontAwesomeIcon
									icon={call.audioMuted || !devicePermissions.microphone ? faMicrophoneSlash : faMicrophone}
									color="#8C8C8C"
									size="lg"
								/>
							}
							size="large"
							onClick={onAudioClick}
						/>
						<Button
							shape="circle"
							icon={
								<FontAwesomeIcon
									icon={call.videoMuted || !devicePermissions.camera ? faVideoSlash : faVideo}
									color="#8C8C8C"
								/>
							}
							size="large"
							onClick={onVideoClick}
						/>
						<Badge count={roomChatOptions.isNewMessages ? <FontAwesomeIcon icon={faPlusCircle} color="#49a31d" /> : 0}>
							<Button
								shape="circle"
								icon={
									<FontAwesomeIcon
										icon={faComments}
										color={roomChatOptions.isOpen ? '#197FF2' : roomChatOptions.isNewMessages ? '#49a31d' : '#8C8C8C'}
									/>
								}
								size="large"
								onClick={onShowRoomChat}
							/>
						</Badge>
						<Button
							shape="circle"
							icon={<FontAwesomeIcon icon={faPhone} color="#CF1322" />}
							size="large"
							onClick={handleLeave}
						/>
						<Button
							shape="circle"
							icon={<FontAwesomeIcon icon={faExclamation} color="#8C8C8C" />}
							size="large"
							onClick={() => setVisibleReportIssueModal(true)}
						/>
					</Space>
				</div>
			</div>
			<ReportIssueModal
				visible={visibleReportIssueModal}
				participant={call.participant}
				onSave={sendReport}
				onClose={() => setVisibleReportIssueModal(false)}
			/>
		</Layout>
	)
}

export default Room
